import ChargeHelper from 'features/Charge/chargeHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import DriverTopUpHistoryFilterView from './components/DriverTopUpHistoryFilterView';
import ToastHelper from 'general/helpers/ToastHelper';
import * as ExcelJS from 'exceljs';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import AgencyTransactionHelper from 'features/ManageAgencyTransaction/agencyTransactionHelper';

DriverTopUpHistoryView.propTypes = {
  hasAppTab: PropTypes.bool,
  driverId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefKey: PropTypes.string,
  driverDetail: PropTypes.object,
};

function DriverTopUpHistoryView({ hasAppTab = false, driverId, prefKey, driverDetail }) {
  // MARK: ---- PROPERTIES ----
  const omitColumnIds = useMemo(() => {
    if (driverId) {
      return ['DRIVER'];
    }
    return [];
  }, [driverId]);
  const defaultColumnIds = useMemo(() => {
    return [
      'TRANSACTION_CODE',
      'TRANSACTION_MOMO_CODE',
      'DRIVER',
      'TRANSACTION_TYPE',
      'AMOUNT',
      'DESCRIPTION',
      'TIME',
      'FUNDING_SOURCE',
      'STATUS',
    ];
  }, []);
  const [loading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState();
  useEffect(() => {
    if (Utils.isValid(prefKey) && !Utils.isValid(driverId)) {
      setFilter(ChargeHelper.Filter.driverTopUpHistoryList);
    } else {
      setFilter({
        userId: driverId,
        q: '',
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        status: '',
        from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
        to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
        dateFilter: dateFilter.baMuoiNgayGanNhat,
        direction: '',
      });
    }
  }, [driverId, prefKey]);
  const [transactionList, setTransactionList] = useState([]);
  const [total, setTotal] = useState(0);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'TRANSACTION_CODE',
        name: UIHelper.buildTableColumn('Mã giao dịch'),
        selector: (row) => row?.walletTransactionCode ?? row?.walletTransactionId,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TRANSACTION_MOMO_CODE',
        name: UIHelper.buildTableColumn('Mã giao dịch tham chiếu'),
        selector: (row) => row?.externalTransactionCode ?? row?.externalTransactionId,
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'DRIVER',
        name: UIHelper.buildTableColumn('Khách hàng'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              Utils.openInNewTab(`${AppRoute.customerList}/${row?.wallet?.userId}`);
            }}
          >
            {row?.wallet?.user?.fullname}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
        omit: Utils.isValid(driverId),
      },
      {
        id: 'TRANSACTION_TYPE',
        name: UIHelper.buildTableColumn('Loại giao dịch'),
        selector: (row) => ChargeHelper.Utils.getDriverTransactionName(row?.fundingSource),
        width: '180px',
        style: tableStyle.columns.normal,
        omit: !Utils.isValid(driverId),
      },
      {
        id: 'AMOUNT',
        name: UIHelper.buildTableColumn('Số tiền', 'VNĐ'),
        selector: (row) =>
          !_.isNil(row?.amount)
            ? (_.toNumber(row?.amount) > 0 ? '+' : '') + Utils.formatNumber(row?.amount)
            : '',
        width: '200px',
        right: true,
        style: tableStyle.columns.normal,
        conditionalCellStyles: [
          {
            when: (row) => _.toNumber(row?.amount) > 0,
            style: {
              color: AppColors.primary,
            },
          },
          {
            when: (row) => _.toNumber(row?.amount) < 0,
            style: {
              color: AppColors.danger,
            },
          },
        ],
      },
      {
        id: 'TIME',
        name: UIHelper.buildTableColumn('Thời gian'),
        selector: (row) =>
          !_.isNil(row?.time) ? Utils.formatDateTimeToString(row?.time, 'DD/MM/YYYY - HH:mm') : '',
        width: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'FUNDING_SOURCE',
        name: UIHelper.buildTableColumn('Phương thức thanh toán'),
        selector: (row) => ChargeHelper.Utils.getChargePaymentMethodName(row?.fundingSource),
        width: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'DESCRIPTION',
        name: UIHelper.buildTableColumn('Nội dung'),
        selector: (row) => {
          return (
            <KTTooltip text={row?.description} offset={[0, 0]}>
              <div>{row?.description}</div>
            </KTTooltip>
          );
        },
        minWidth: '300px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái giao dịch'),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getWalletTransactionStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getWalletTransactionStatusColor(
                row?.status
              )}`}
            />
          );
        },
        width: '180px',
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: '80px',
        center: true,
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, []);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  useEffect(() => {
    if (Utils.isValid(filter)) {
      setIsLoading(true);
      let params = _.pickBy(
        filter,
        (value) => value !== null && value !== undefined && value !== ''
      );
      params.transactionId = null;
      if (Utils.isValid(driverId)) {
        ChargeHelper.API.Driver.requestGetDriverTransactionHistory(driverId, params)
          .then((response) => {
            setTransactionList(response?.transactions || []);
            setTotal(response?.transactionCount ?? 0);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        // ChargeHelper.API.Payment.requestGetPaymentList(params)
        //   .then((response) => {
        //     setTransactionList(response?.payments);
        //     setTotal(response?.total);
        //   })
        //   .finally(() => {
        //     setIsLoading(false);
        //   });
        ChargeHelper.API.WalletTransaction.requestGetWalletTransactionList(params)
          .then((response) => {
            setTransactionList(response?.walletTransactions);
            setTotal(response?.total);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [filter]);
  const windowSize = useWindowSize();

  // MARK: ---- METHODS ----
  const handleRefresh = (newFilter) => {
    if (Utils.isValid(prefKey)) {
      ChargeHelper.Filter.driverTopUpHistoryList = {
        ...ChargeHelper.Filter.driverTopUpHistoryList,
        ...newFilter,
      };
      setFilter({ ...ChargeHelper.Filter.driverTopUpHistoryList });
    } else {
      setFilter(newFilter);
    }
  };
  const buildMoreActions = (row) => {
    const actions = [];
    // xem chi tiet
    actions.push({
      text: 'Xem chi tiết',
      iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
      click: () => {
        ChargeHelper.UI.Driver.showDialogTransactionDetail(row, null);
      },
    });
    return actions;
  };

  // xuat file excel
  const handleExportExcel = async (exportData, params) => {
    let exportDataList = exportData;
    if (_.isEmpty(exportDataList)) {
      ToastHelper.showError('Không có dữ liệu để tải xuống');
      return;
    }

    const sheetFileName = 'Giao_dịch_KH_nạp_tiền_' + moment(params.from).format('DDMMYYYY') + '_' + moment(params.to).format('DDMMYYYY');
    const ext = '.xlsx';
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetFileName, {
      headerFooter: { firstHeader: sheetFileName },
    });

    // Kiểm tra driverId
    const hasDriverId = !!driverId;
    const lastColumn = hasDriverId ? 'J' : 'M';
    sheet.mergeCells(`A1:${lastColumn}1`);
    sheet.getCell('A1').value = `${hasDriverId ? `LỊCH SỬ NẠP TIỀN _${driverDetail?.fullname?.toUpperCase()}` : "DANH SÁCH GIAO DỊCH KHÁCH HÀNG NẠP TIỀN"} (NGÀY ${moment(params.from).format(
      'DD/MM/YYYY'
    )} - ${moment(params.to).format('DD/MM/YYYY')})`;
    sheet.getCell('A1').font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '29618E' },
      color: { argb: 'FFFFFF' },
    };
    sheet.getCell('A1').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    sheet.getRow(1).height = 25;

    const columns = [
      { key: 'stt', width: 7 },
      { key: 'transactionCode', width: 18 },
      { key: 'externalTransactionId', width: 25 },
      { key: 'time', width: 22 },
      { key: 'customerCode', width: 22 },
      { key: 'customerName', width: 30 },
      ...(hasDriverId ? [] : [
        { key: 'cccd', width: 22 },
        { key: 'phone', width: 22 },
        { key: 'email', width: 25 },
      ]),
      { key: 'topupAmount', width: 20 },
      { key: 'fundingSource', width: 25 },
      { key: 'content', width: 30 },
      { key: 'status', width: 20 },
    ];
    sheet.columns = columns;

    sheet.getRow(2).height = 30;
    sheet.getRow(2).font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
    };
    sheet.getRow(2).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };

    sheet.getCell('A2').value = 'STT';
    sheet.getCell('B2').value = 'Mã giao dịch';
    sheet.getCell('C2').value = 'Mã giao dịch tham chiếu';
    sheet.getCell('D2').value = 'Thời gian';
    sheet.getCell('E2').value = 'Mã khách hàng';
    sheet.getCell('F2').value = 'Tên khách hàng';
    if (!hasDriverId) {
      sheet.getCell('G2').value = 'Số CCCD của KH';
      sheet.getCell('H2').value = 'Số điện thoại';
      sheet.getCell('I2').value = 'Email';
      sheet.getCell('J2').value = 'Số tiền nạp (VNĐ)';
      sheet.getCell('K2').value = 'Phương thức thanh toán';
      sheet.getCell('L2').value = 'Nội dung';
      sheet.getCell('M2').value = 'Trạng thái';
    } else {
      sheet.getCell('G2').value = 'Số tiền nạp';
      sheet.getCell('H2').value = 'Phương thức thanh toán';
      sheet.getCell('I2').value = 'Nội dung';
      sheet.getCell('J2').value = 'Trạng thái';
    }

    const headerRange = `A2:${lastColumn}2`;
    sheet.getRow(2).eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    let i = 1;
    exportDataList?.forEach((row) => {
      const dataRow = sheet.addRow({
        stt: i++,
        transactionCode: row?.walletTransactionCode ?? row?.walletTransactionId,
        externalTransactionId: row?.externalTransactionCode ?? row?.externalTransactionId,
        time: !_.isNil(row?.time) ? Utils.formatDateTimeToString(row?.time, 'DD/MM/YYYY - HH:mm') : '',
        customerCode: hasDriverId ? driverDetail?.userCode : row?.wallet?.user?.userCode ?? row?.wallet?.user?.userId,
        customerName: hasDriverId ? driverDetail?.fullname : row?.wallet?.user?.fullname,
        ...(hasDriverId ? {} : {
          cccd: row?.wallet?.user?.idCardNumber,
          phone: row?.wallet?.user?.phone,
          email: row?.wallet?.user?.email,
        }),
        topupAmount: Utils.formatNumber(row?.amount ?? 0),
        fundingSource: ChargeHelper.Utils.getChargePaymentMethodName(row?.fundingSource),
        content: row?.description,
        status: ChargeHelper.Utils.getWalletTransactionStatusName(row?.status),
      });

      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (colNumber === 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        } else if (colNumber === (hasDriverId ? 7 : 10)) {
          cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        }
      });
    });

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 3) {
        row.font = {
          name: 'Times New Roman',
          family: 4,
          size: 11,
          bold: false,
        };
      }
    });

    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = sheetFileName + ext;
      anchor.click();
      window.URL.revokeObjectURL(url);
      ToastHelper.showSuccess('Tải xuống file Excel thành công');
      AppCustomDialogHelper.hide();
    });
  };

  return (
    <div className="pb-4">
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            {!hasAppTab && (
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Giao dịch KH/đối tác nạp tiền
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(total)} giao dịch
                  </span>
                </span>
              </div>
            )}
            {/* actions */}
            <div className="d-flex align-items-center">
              {!driverId && UIHelper.buildBlueButton(
                'Tải xuống',
                async () => {
                  try {
                    ChargeHelper.UI.Excel.showDialogDownloadExcelFile(
                      ChargeHelper.Filter.driverTopUpHistoryList,
                      async (params) => {
                        try {
                          const { walletTransactions = [] } = await ChargeHelper.API.WalletTransaction.requestGetWalletTransactionList(params);
                          return handleExportExcel(walletTransactions, params);
                        } catch (error) {
                          ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                          return [];
                        }
                      },
                    );
                  } catch (error) {
                    ToastHelper.showError(`Lỗi khi khởi tạo tải xuống: ${error.message}`);
                  }
                },
                <i className="fa-regular fa-download" style={{ fontSize: 15 }} />
              )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="paymentHistory_search"
                  placeholder="Mã giao dịch, nội dung"
                  value={filter?.q}
                  onSubmit={(text) => {
                    handleRefresh({ ...filter, q: text, page: 0 });
                  }}
                />
              </div>
              {/* trang thai */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Trạng thái')}
                <KTBSDropdown
                  name="paymentHistory_status"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        ChargeHelper.Data.walletTransactionStatusOptions,
                        (item) => item.value === filter?.status
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={filter?.status}
                  dropdownMenuItems={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.walletTransactionStatusOptions,
                  ]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    handleRefresh({ ...filter, status: value, page: 0 });
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* loc theo khoang thoi gian */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Khoảng thời gian')}
                <CustomDateRangePicker
                  toggleElementBorderRadius={5}
                  initialValue={filter?.dateFilter}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    handleRefresh({
                      ...filter,
                      dateFilter: selectedValue,
                      from: startDate,
                      to: endDate,
                      page: 0,
                    });
                  }}
                />
              </div>
              {/* loai giao dich */}
              {/* <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Loại giao dịch')}
                <KTBSDropdown
                  name="paymentHistory_direction"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        AgencyTransactionHelper.transactionTypeOptions(),
                        (item) => item.value === filter?.direction
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={filter?.direction}
                  dropdownMenuItems={AgencyTransactionHelper.transactionTypeOptions()}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    handleRefresh({ ...filter, direction: value, page: 0 });
                  }}
                  isDropdownForm={true}
                />
              </div> */}
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                {/* <ButtonFilterDropdown
                  dropdownContent={
                    <DriverTopUpHistoryFilterView
                      initialFilter={filter}
                      onCancel={(newFilter) => {
                        handleRefresh(newFilter);
                      }}
                      onApply={(newFilter) => {
                        handleRefresh(newFilter);
                      }}
                    />
                  }
                /> */}
                {prefKey && (
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                )}

                {driverId && UIHelper.buildBlueButton(
                  'Tải xuống',
                  async () => {
                    try {
                      ChargeHelper.UI.Excel.showDialogDownloadExcelFile(
                        filter,
                        async (params) => {
                          try {
                            const { topups = [] } = await ChargeHelper.API.Driver.requestGetDriverTransactionHistory(driverId, params)
                            return handleExportExcel(topups, params);
                          } catch (error) {
                            ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                            return [];
                          }
                        },
                      );
                    } catch (error) {
                      ToastHelper.showError(`Lỗi khi khởi tạo tải xuống: ${error.message}`);
                    }
                  },
                  <i className="fa-regular fa-download" style={{ fontSize: 15 }} />
                )}
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={transactionList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData('Không có giao dịch nạp tiền nào.')}
              />
            </div>

            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={total}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={transactionList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  handleRefresh({ ...filter, page: iNewPage - 1 });
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  handleRefresh({ ...filter, limit: iNewRowsPerPage, page: 0 });
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="giao dịch"
              />
            </div>
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default DriverTopUpHistoryView;
